/**
 * Nav slide tint — the header borrows the colours of the hero slide behind it.
 *
 * The header and the hero are separate sections (header group vs template), so
 * they cannot see each other in Liquid, and the slide on screen changes at
 * runtime. This bridges them: whenever the visible slide changes it copies that
 * slide's resolved background and text colours onto the header host as CSS
 * variables, and site-header.liquid decides what to do with them.
 *
 * Which slide is showing, and how its colours are read, lives in
 * assets/slide-tint-core.js — see the notes there for why it is done by geometry
 * and by a frame loop rather than by carousel events.
 *
 * Contract:
 * - Opt in with `data-nav-tint` on the `<slide-carousel>` (Hero slideshow →
 *   "Match the header to the current slide").
 * - Writes `--fit-nav-tint-bg`, `--fit-nav-tint-bg-rgb`, `--fit-nav-tint-fg`
 *   and `--fit-nav-tint-fg-rgb` on `<site-header>`, plus a
 *   `data-nav-tint-active` attribute while the tint should apply.
 * - The attribute is removed once the hero no longer sits behind the header, so
 *   a fixed header stops wearing hero colours over the rest of the page.
 */

import { activeSlide, readSlideColors } from './slide-tint-core.js';

const SOURCE_SELECTOR = 'slide-carousel[data-nav-tint]';
const HEADER_SELECTOR = 'site-header';

let source = null;
let scroller = null;
let header = null;
let observer = null;
let frame = null;
let listening = false;
let lastOffset = null;

/** True while the hero still passes behind the header band. */
function heroIsBehindHeader() {
  if (!source || !header) return false;

  // Measure the row, not the host: an Interactive Island host reserves no height
  // on desktop (`--header-height: 0px`), so the host's box is a flat line at the
  // top of the page and would report an overlap forever.
  const band = header.querySelector('.header__row') ?? header;
  const bandBox = band.getBoundingClientRect();
  const heroBox = source.getBoundingClientRect();

  // A hidden scroll-up header collapses to nothing; keep the last answer rather
  // than flickering the tint off while it animates away.
  if (bandBox.height === 0) return header.hasAttribute('data-nav-tint-active');

  return heroBox.bottom > bandBox.top && heroBox.top < bandBox.bottom;
}

/** Reads the slide on screen and hands its colours to the header. */
function paintFromSlide() {
  const colors = readSlideColors(activeSlide(source));
  if (!colors) return false;

  header.style.setProperty('--fit-nav-tint-bg', colors.background.value);
  header.style.setProperty('--fit-nav-tint-bg-rgb', colors.background.rgb);
  header.style.setProperty('--fit-nav-tint-fg', colors.foreground.value);
  header.style.setProperty('--fit-nav-tint-fg-rgb', colors.foreground.rgb);
  return true;
}

/** Recolour if the carousel has moved, then settle whether the tint applies. */
function sync({ force = false } = {}) {
  if (!header) return false;

  const offset = scroller?.scrollLeft ?? 0;
  const moved = force || offset !== lastOffset;
  lastOffset = offset;

  const painted = moved ? paintFromSlide() : header.style.getPropertyValue('--fit-nav-tint-bg') !== '';

  if (painted && heroIsBehindHeader()) header.setAttribute('data-nav-tint-active', '');
  else header.removeAttribute('data-nav-tint-active');

  // The island's resting colours are the brand ones now, and transparency is the
  // exception — so the absence of the attribute above has to mean "past the
  // hero", not "the script has not run yet". Those two are indistinguishable
  // until the first decision lands, and reading the ambiguous frames as "past
  // the hero" would flash a solid band across the top of the hero on every load.
  // This marks the end of that grace period; the stylesheet holds the island
  // transparent until it appears.
  header.setAttribute('data-nav-tint-resolved', '');

  return painted;
}

/** Runs only while the hero is on screen — one scroll offset read per frame. */
function tick() {
  frame = null;
  sync();
  if (observer) frame = requestAnimationFrame(tick);
}

function start() {
  if (frame == null) frame = requestAnimationFrame(tick);
}

function stop() {
  if (frame != null) cancelAnimationFrame(frame);
  frame = null;
  header?.removeAttribute('data-nav-tint-active');
}

function listen() {
  if (listening) return;
  listening = true;

  // Resize can change which slide is centred and how tall the header is; a pick
  // covers arrows, dots and autoplay for the frame before the loop next reads.
  document.addEventListener('carousel:pick', () => sync({ force: true }));
  addEventListener('resize', () => sync({ force: true }), { passive: true });
}

function init() {
  observer?.disconnect();
  observer = null;
  stop();
  lastOffset = null;

  source = document.querySelector(SOURCE_SELECTOR);
  header = document.querySelector(HEADER_SELECTOR);
  scroller = source?.querySelector('carousel-slides') ?? source;

  if (!source || !header) return;

  listen();
  sync({ force: true });

  // Idle whenever the hero is off screen, so no page pays for a frame loop it
  // cannot see the result of.
  observer = new IntersectionObserver(
    (entries) => {
      if (entries.some((entry) => entry.isIntersecting)) start();
      else stop();
    },
    { threshold: 0 },
  );
  observer.observe(source);
}

init();

// The theme editor swaps section markup in place, which replaces both the
// carousel and the header host, so re-resolve them on every section load.
document.addEventListener('shopify:section:load', init);
